import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { createMyAddress, deleteMyAddress, fetchMyAddresses, updateMyAddress, } from '@/api/addresses';
import { formatStructuredAddressDisplay } from '@/types/address';
function emptyAddr() {
    return { city: '', street: '', house: '', entrance: null, floor: null, apartment: null };
}
function fromMy(a) {
    const x = a.address;
    return {
        city: x.city,
        street: x.street,
        house: x.house,
        entrance: x.entrance ?? null,
        floor: x.floor ?? null,
        apartment: x.apartment ?? null,
    };
}
export function UserAddressesPage() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newDraft, setNewDraft] = useState(emptyAddr());
    const [newDefault, setNewDefault] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(emptyAddr());
    const [editDefault, setEditDefault] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        setError('');
        fetchMyAddresses()
            .then(setList)
            .catch(() => setError('Не удалось загрузить адреса'))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    const setNewField = (key, value) => {
        if (key === 'entrance' || key === 'floor' || key === 'apartment') {
            setNewDraft((p) => ({ ...p, [key]: value === '' ? null : value }));
        }
        else {
            setNewDraft((p) => ({ ...p, [key]: value }));
        }
    };
    const setEditField = (key, value) => {
        if (key === 'entrance' || key === 'floor' || key === 'apartment') {
            setEditDraft((p) => ({ ...p, [key]: value === '' ? null : value }));
        }
        else {
            setEditDraft((p) => ({ ...p, [key]: value }));
        }
    };
    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newDraft.city.trim() || !newDraft.street.trim() || !newDraft.house.trim())
            return;
        setSaving(true);
        setError('');
        try {
            await createMyAddress({
                city: newDraft.city.trim(),
                street: newDraft.street.trim(),
                house: newDraft.house.trim(),
                entrance: newDraft.entrance?.trim() || null,
                floor: newDraft.floor?.trim() || null,
                apartment: newDraft.apartment?.trim() || null,
            }, newDefault);
            setNewDraft(emptyAddr());
            setNewDefault(false);
            await load();
        }
        catch {
            setError('Не удалось сохранить адрес');
        }
        finally {
            setSaving(false);
        }
    };
    const startEdit = (a) => {
        setEditingId(a.id);
        setEditDraft(fromMy(a));
        setEditDefault(a.isDefault);
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft(emptyAddr());
        setEditDefault(false);
    };
    const handleDelete = async (id) => {
        if (!window.confirm('Удалить этот адрес?'))
            return;
        setSaving(true);
        setError('');
        try {
            await deleteMyAddress(id);
            if (editingId === id)
                cancelEdit();
            await load();
        }
        catch {
            setError('Не удалось удалить адрес');
        }
        finally {
            setSaving(false);
        }
    };
    const handleSaveEdit = async (e) => {
        e.preventDefault();
        if (editingId == null)
            return;
        if (!editDraft.city.trim() || !editDraft.street.trim() || !editDraft.house.trim())
            return;
        setSaving(true);
        setError('');
        try {
            await updateMyAddress(editingId, {
                city: editDraft.city.trim(),
                street: editDraft.street.trim(),
                house: editDraft.house.trim(),
                entrance: editDraft.entrance?.trim() || null,
                floor: editDraft.floor?.trim() || null,
                apartment: editDraft.apartment?.trim() || null,
            }, editDefault);
            cancelEdit();
            await load();
        }
        catch {
            setError('Не удалось обновить адрес');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx("h1", { className: "user-panel__title", children: "\u0410\u0434\u0440\u0435\u0441\u0430" }), _jsx("p", { className: "user-muted", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0435 \u0430\u0434\u0440\u0435\u0441\u0430 \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0438" }), error ? _jsx("p", { className: "user-muted", style: { color: '#991b1b' }, children: error }) : null, loading ? (_jsx("p", { className: "user-muted", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" })) : (_jsx("ul", { className: "user-address-list", children: list.map((a) => (_jsx("li", { className: "user-address-card", children: editingId === a.id ? (_jsxs("form", { className: "user-form", onSubmit: handleSaveEdit, children: [_jsx("label", { children: "\u0413\u043E\u0440\u043E\u0434 *" }), _jsx("input", { value: editDraft.city, onChange: (e) => setEditField('city', e.target.value), required: true }), _jsx("label", { children: "\u0423\u043B\u0438\u0446\u0430 *" }), _jsx("input", { value: editDraft.street, onChange: (e) => setEditField('street', e.target.value), required: true }), _jsx("label", { children: "\u0414\u043E\u043C *" }), _jsx("input", { value: editDraft.house, onChange: (e) => setEditField('house', e.target.value), required: true }), _jsx("label", { children: "\u041F\u043E\u0434\u044A\u0435\u0437\u0434" }), _jsx("input", { value: editDraft.entrance ?? '', onChange: (e) => setEditField('entrance', e.target.value) }), _jsx("label", { children: "\u042D\u0442\u0430\u0436" }), _jsx("input", { value: editDraft.floor ?? '', onChange: (e) => setEditField('floor', e.target.value) }), _jsx("label", { children: "\u041A\u0432\u0430\u0440\u0442\u0438\u0440\u0430" }), _jsx("input", { value: editDraft.apartment ?? '', onChange: (e) => setEditField('apartment', e.target.value) }), _jsx("div", { className: "user-form-row", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: editDefault, onChange: (e) => setEditDefault(e.target.checked) }), ' ', "\u041F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E"] }) }), _jsxs("div", { className: "user-address-actions", children: [_jsx("button", { type: "submit", className: "user-btn user-btn--primary", disabled: saving, children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" }), _jsx("button", { type: "button", className: "user-btn", onClick: cancelEdit, disabled: saving, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "user-address-card__row", children: _jsx("p", { className: "user-address-card__text", children: formatStructuredAddressDisplay(a.address) }) }), a.isDefault ? _jsx("span", { className: "user-badge", children: "\u041F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E" }) : null, _jsxs("div", { className: "user-address-actions", children: [_jsx("button", { type: "button", className: "user-btn", onClick: () => startEdit(a), children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" }), _jsx("button", { type: "button", className: "user-btn user-btn--danger", onClick: () => void handleDelete(a.id), disabled: saving, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] })] })) }, a.id))) })), _jsx("h2", { className: "user-panel__title", style: { fontSize: '1.15rem', marginTop: '1.5rem' }, children: "\u041D\u043E\u0432\u044B\u0439 \u0430\u0434\u0440\u0435\u0441" }), _jsxs("form", { className: "user-form", onSubmit: handleAdd, children: [_jsx("label", { htmlFor: "new-city", children: "\u0413\u043E\u0440\u043E\u0434 *" }), _jsx("input", { id: "new-city", value: newDraft.city, onChange: (e) => setNewField('city', e.target.value) }), _jsx("label", { htmlFor: "new-street", children: "\u0423\u043B\u0438\u0446\u0430 *" }), _jsx("input", { id: "new-street", value: newDraft.street, onChange: (e) => setNewField('street', e.target.value) }), _jsx("label", { htmlFor: "new-house", children: "\u0414\u043E\u043C *" }), _jsx("input", { id: "new-house", value: newDraft.house, onChange: (e) => setNewField('house', e.target.value) }), _jsx("label", { htmlFor: "new-entrance", children: "\u041F\u043E\u0434\u044A\u0435\u0437\u0434" }), _jsx("input", { id: "new-entrance", value: newDraft.entrance ?? '', onChange: (e) => setNewField('entrance', e.target.value) }), _jsx("label", { htmlFor: "new-floor", children: "\u042D\u0442\u0430\u0436" }), _jsx("input", { id: "new-floor", value: newDraft.floor ?? '', onChange: (e) => setNewField('floor', e.target.value) }), _jsx("label", { htmlFor: "new-apt", children: "\u041A\u0432\u0430\u0440\u0442\u0438\u0440\u0430" }), _jsx("input", { id: "new-apt", value: newDraft.apartment ?? '', onChange: (e) => setNewField('apartment', e.target.value) }), _jsx("div", { className: "user-form-row", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: newDefault, onChange: (e) => setNewDefault(e.target.checked) }), ' ', "\u0421\u0434\u0435\u043B\u0430\u0442\u044C \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E"] }) }), _jsx("button", { type: "submit", className: "user-btn user-btn--primary", disabled: saving ||
                            loading ||
                            !newDraft.city.trim() ||
                            !newDraft.street.trim() ||
                            !newDraft.house.trim(), children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" })] })] }));
}
